import * as IoC from 'typescript-ioc';
import { IApiClient } from '../../infrastructure/api-client';
import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { IEventRequestParams } from '../shared.dto';
import * as CONNECTIONS_API from '../../constants/api/connections-api.constants';
import {
    ConnectionType,
    IAudioBridge,
    IAudioBridgeDTO,
    IChildConnectionDTO,
    IConfigurePassportDisplayDTO,
    IConfigurePassportDisplayParams,
    IConnectionDetailsDTO,
    IConnectionGroupDTO,
    IConnectionsDAO,
    IConnectionTypesDTO,
    IDeleteConnectionRequestParams,
    IKnovioLiveStreamDTO,
    IMeetingConnectionIdParam,
    IMeetingConnectionRequestParams,
    IMeetingConnectionsDTO,
    IMeetingConnectionsRequestParams,
    IMeetingConnectionsSingleEntityDTO,
    IMeetingConnectionsSingleResponceDTO,
    IMeetingsOperatorsDTO,
    IOperatorsBulkUpdateDTO,
    IPanelistLinkConnectionInfo,
    IPanelistLinkResponse,
    IRoomConnectionRequestParams,
    IRoomConnectionsDTO,
    IRoomConnectionsRequestParams,
    IRoomMeetingConnectionsDTO,
    ISyncSpeaekrsToKnovioDTO,
    IUpdateMeetingConnectionGroup,
    IUpdateRoomConnectionGroup,
    IWebinarParticipantConnections,
    TGenerateConnectionsRequest,
} from '../connections.dao';
import { Permission } from '../roles.dao';
import { IConnectionExportTemplate } from '../users.dao';
import { JobDtoType } from '../../domain/jobs';
import { MeetingApiParams } from '../../components/_events/_manage-event/shared/connections-list/_connection-form/connection-form.viewmodel';
import { CREATE_AUDIO_BRIDGE, CREATE_PANELIST_LINK } from '../../constants/api/zoom-api.constants';
import { ConnectionVersion } from '../../constants/common.constants';

const normalizeAudioBridgeRecordingFlag = (connection: IConnectionDetailsDTO): IConnectionDetailsDTO => {
    if (connection?.type !== ConnectionType.AudioBridge) {
        return connection;
    }
    const { recordCalls, ...rest } = connection as IConnectionDetailsDTO & { recordCalls?: boolean };
    return { ...rest, automaticallyRecordEnabled: recordCalls };
};

class ConnectionsDAO implements IConnectionsDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter) {
    }

    public async getMeetingConnections(params: IMeetingConnectionRequestParams): Promise<IMeetingConnectionsDTO> {
        const response = await this.apiClient.get<IMeetingConnectionsDTO>(
            {
                url: CONNECTIONS_API.MEETING_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        // eslint-disable-next-line no-unused-vars
        const { heroImage, thumbnailImage, heroImageWithUrl, thumbnailImageWithUrl, ...parsedResponse } = response.parsedResponse;

        return {
            ...parsedResponse,
            ...(parsedResponse.connections && {
                connections: parsedResponse.connections.map(normalizeAudioBridgeRecordingFlag),
            }),
        };
    }

    public async getRoomsConnectionsList(params: IRoomConnectionsRequestParams): Promise<IRoomConnectionsDTO[]> {
        const response = await this.apiClient.get<IRoomConnectionsDTO[]>(
            {
                url: CONNECTIONS_API.ROOMS_CONNECTION_LIST_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            {
                ...params,
                startDateTime: this.apiDateConverter.dateToAPIString(params.startDateTime)
            });
        return response.parsedResponse;
    }

    public async getMeetingConnectionsByRoom(params: IMeetingConnectionsRequestParams): Promise<IRoomMeetingConnectionsDTO[]> {
        const paramsObj = !params.startDateTime ? params : { ...params, startDateTime: this.apiDateConverter.dateToAPIString(params.startDateTime) };
        const response = await this.apiClient.get<IRoomMeetingConnectionsDTO[]>(
            {
                url: CONNECTIONS_API.MEETING_BY_ROOM_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            paramsObj
        );
        return response.parsedResponse;
    }

    public async getConnectionDatesList(params: IEventRequestParams): Promise<{ dates: Date[] }> {
        const response = await this.apiClient.get<{ dates: Date[] }>(
            {
                url: CONNECTIONS_API.ROOM_START_DATES_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public async getConnectionTypes(params: IEventRequestParams): Promise<IConnectionTypesDTO> {
        const response = await this.apiClient.get<IConnectionTypesDTO>(CONNECTIONS_API.CONNECTION_TYPES_URL, params);
        return response.parsedResponse;
    }

    public async updateMeetingConnections(params: IEventRequestParams & IMeetingConnectionsDTO): Promise<IMeetingConnectionsDTO> {
        const response = await this.apiClient.put<IMeetingConnectionsDTO>(
            {
                url: CONNECTIONS_API.MEETING_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public async createMeetingConnection(params: IMeetingConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> {
        const response = await this.apiClient.post<IConnectionDetailsDTO>(
            {
                url: CONNECTIONS_API.CREATE_MEETING_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public editMeetingConnection = async (params: IMeetingConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
        const isV2PanelistChild = params.connectionVersion === ConnectionVersion.V2 && params.type === ConnectionType.PanelistLink;
        const response = await this.apiClient.put<IConnectionDetailsDTO>(
            {
                url: isV2PanelistChild ? CONNECTIONS_API.CHILD_CONNECTION_URL : CONNECTIONS_API.EDIT_MEETING_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    };

    public async deleteMeetingConnection(params: IDeleteConnectionRequestParams & IMeetingConnectionRequestParams): Promise<IMeetingConnectionsDTO> {
        const response = await this.apiClient.delete<IMeetingConnectionsDTO>(
            {
                url: CONNECTIONS_API.DELETE_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public async createRoomConnection(params: IRoomConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> {
        const response = await this.apiClient.post<IConnectionDetailsDTO>(
            {
                url: CONNECTIONS_API.CREATE_ROOM_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public editRoomConnection = async (params: IRoomConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
        const response = await this.apiClient.put<IConnectionDetailsDTO>(
            {
                url: CONNECTIONS_API.EDIT_DELETE_ROOM_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    };

    public async deleteRoomConnection(params: IRoomConnectionRequestParams & IDeleteConnectionRequestParams): Promise<void> {
        const response = await this.apiClient.delete<void>(
            {
                url: CONNECTIONS_API.EDIT_DELETE_ROOM_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public async getConnectionsCSV(params: IEventRequestParams) {
        await this.apiClient.getFile(
            {
                url: CONNECTIONS_API.CSV_DOWNLOAD_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
    }

    public async getAllEventConnectionsCSV(params: IEventRequestParams & IConnectionExportTemplate) {
        await this.apiClient.getFile(CONNECTIONS_API.CSV_EVENT_CONNECTIONS_DOWNLOAD_URL, params, {
            requestInit: {
                method: 'POST'
            }
        });
    }

    public async getWebinarParticipantConnections(params: IMeetingConnectionRequestParams & IEventRequestParams): Promise<IWebinarParticipantConnections> {
        const response = await this.apiClient.get<IWebinarParticipantConnections>(
            {
                url: CONNECTIONS_API.WEBINAR_REGISTRANTS_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public getKnovioConnectionDetails = async (params: IMeetingConnectionRequestParams & IMeetingConnectionIdParam): Promise<IConnectionDetailsDTO> => {
        const response = await this.apiClient.get<IConnectionDetailsDTO>(
            {
                url: CONNECTIONS_API.GET_KNOVIO_CONNECTION,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    };

    public getKnovioRoomConnectionDetails = async (params: IRoomConnectionRequestParams & IMeetingConnectionIdParam): Promise<IConnectionDetailsDTO> => {
        const response = await this.apiClient.get<IConnectionDetailsDTO>(
            {
                url: CONNECTIONS_API.GET_KNOVIO_ROOM_CONNECTION,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    };

    public async operatorsBulkUpdate(params: IEventRequestParams & IOperatorsBulkUpdateDTO): Promise<JobDtoType> {
        const response = await this.apiClient.put<JobDtoType>(
            {
                url: CONNECTIONS_API.BULK_UPDATE_MEETING_OPERATORS_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public async updateMeetingConnectionsWithSingleEntity(
        params: IEventRequestParams & IMeetingConnectionsSingleEntityDTO
    ): Promise<IMeetingConnectionsSingleResponceDTO> {
        const response = await this.apiClient.put<IMeetingConnectionsSingleResponceDTO>(
            {
                url: CONNECTIONS_API.SINGLE_MEETING_CONNECTION_URL,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public async getConnectionsOperatorsByMeetingIds(params: IEventRequestParams & { meetingIds: string }): Promise<IMeetingsOperatorsDTO> {
        const response = await this.apiClient.get<IMeetingsOperatorsDTO>(CONNECTIONS_API.MEETINGS_CONNECTIONS_OPERATORS_URL, params);
        return response.parsedResponse;
    }

    public async createKnovioLiveStream(params: MeetingApiParams, payload: IKnovioLiveStreamDTO): Promise<IConnectionDetailsDTO> {
        // eslint-disable-next-line no-unused-vars
        const { roomId, meetingId, ...rest } = params;
        const queryParams = meetingId ? `?meetingId=${meetingId}` : `?roomId=${roomId}`;
        const response = await this.apiClient.post<IConnectionDetailsDTO>(
            {
                url: CONNECTIONS_API.CREATE_KNOVIO_LIVE_STREAM_URL + queryParams,
                permission: Permission.CREATE_KNOVIO_LIVE_STREAMS
            },
            {
                ...rest,
                ...payload
            }
        );
        return response.parsedResponse;
    }

    public async updateKnovioLiveStream(params: MeetingApiParams, payload: IKnovioLiveStreamDTO): Promise<{ message: string }> {
        // eslint-disable-next-line no-unused-vars
        const { roomId, meetingId, ...rest } = params;
        const response = await this.apiClient.post<{ message: string }>(
            {
                url: CONNECTIONS_API.UPDATE_KNOVIO_LIVE_STEAM_URL,
                permission: Permission.EDIT_KNOVIO_LIVE_STREAMS
            },
            {
                ...rest,
                ...payload
            }
        );
        return response.parsedResponse;
    }

    public async generateConnections(params: TGenerateConnectionsRequest): Promise<JobDtoType> {
        const response = await this.apiClient.post<JobDtoType>(CONNECTIONS_API.GENERATE_CONNECTIONS, params);
        return response.parsedResponse;
    }
    public async syncSpeakersToKnovio(params: MeetingApiParams & ISyncSpeaekrsToKnovioDTO): Promise<ISyncSpeaekrsToKnovioDTO> {
        const response = await this.apiClient.post<ISyncSpeaekrsToKnovioDTO>(CONNECTIONS_API.SYNC_SPEAKERS_TO_KNOVIO, params);
        return response.parsedResponse;
    };

    public async deleteUniqueJoiningLinksConnections(params: IEventRequestParams & { meetingId: number }): Promise<JobDtoType> {
        const response = await this.apiClient.get<JobDtoType>(
            {
                url: CONNECTIONS_API.DELETE_UNIQUE_JOINING_LINKS,
                permission: Permission.CONNECTIONS_MANAGEMENT
            },
            params
        );
        return response.parsedResponse;
    }

    public async scheduleNewAudioBridge(params: IEventRequestParams & IAudioBridgeDTO): Promise<IAudioBridge> {
        const response = await this.apiClient.post<IAudioBridge>(CREATE_AUDIO_BRIDGE, {
            ...params,
            startTime: this.apiDateConverter.dateToAPIString(params.startTime, params.timeZone),
            endTime: this.apiDateConverter.dateToAPIString(params.endTime, params.timeZone),
        });
        return response.parsedResponse;
    }

    public async createMeetingConnectionGroup(params: IMeetingConnectionRequestParams & IConnectionGroupDTO): Promise<IConnectionGroupDTO> {
        const response = await this.apiClient.post<IConnectionGroupDTO>(CONNECTIONS_API.MEETING_CONNECTION_GROUP_URL, params);
        return response.parsedResponse;
    }

    public async editMeetingConnectionGroup(params: IUpdateMeetingConnectionGroup & IConnectionGroupDTO): Promise<IConnectionGroupDTO> {
        const response = await this.apiClient.put<IConnectionGroupDTO>(CONNECTIONS_API.EDIT_MEETING_CONNECTION_GROUP_URL, params);
        return response.parsedResponse;
    }

    public async deleteMeetingConnectionGroup(params: IUpdateMeetingConnectionGroup): Promise<void> {
        const response = await this.apiClient.delete<void>(CONNECTIONS_API.EDIT_MEETING_CONNECTION_GROUP_URL, params);
        return response.parsedResponse;
    }

    public async createRoomConnectionGroup(params: IRoomConnectionRequestParams & IConnectionGroupDTO): Promise<IConnectionGroupDTO> {
        const response = await this.apiClient.post<IConnectionGroupDTO>(CONNECTIONS_API.ROOM_CONNECTION_GROUP_URL, params);
        return response.parsedResponse;
    }

    public async editRoomConnectionGroup(params: IUpdateRoomConnectionGroup & IConnectionGroupDTO): Promise<IConnectionGroupDTO> {
        const response = await this.apiClient.put<IConnectionGroupDTO>(CONNECTIONS_API.EDIT_ROOM_CONNECTION_GROUP_URL, params);
        return response.parsedResponse;
    }

    public async deleteRoomConnectionGroup(params: IUpdateRoomConnectionGroup): Promise<void> {
        const response = await this.apiClient.delete<void>(CONNECTIONS_API.EDIT_ROOM_CONNECTION_GROUP_URL, params);
        return response.parsedResponse;
    }

    public getConnectionPassportConfigureDisplay = async (params: IConfigurePassportDisplayParams): Promise<IConfigurePassportDisplayDTO> => {
        const response = await this.apiClient.get<IConfigurePassportDisplayDTO>(CONNECTIONS_API.CONNECTION_PASSPORT_CONFIGURE_DISPLAY, params);
        return response.parsedResponse;
    };

    public updateConnectionPassportConfigureDisplay = async (params: IConfigurePassportDisplayDTO): Promise<IConfigurePassportDisplayDTO> => {
        const response = await this.apiClient.post<IConfigurePassportDisplayDTO>(CONNECTIONS_API.CONNECTION_PASSPORT_CONFIGURE_DISPLAY, params);
        return response.parsedResponse;
    };

    public getRoomConnectionPassportConfigureDisplay = async (params: IConfigurePassportDisplayParams): Promise<IConfigurePassportDisplayDTO> => {
        const response = await this.apiClient.get<IConfigurePassportDisplayDTO>(CONNECTIONS_API.ROOM_PASSPORT_CONFIGURE_DISPLAY, params);
        return response.parsedResponse;
    };

    public updateRoomConnectionPassportConfigureDisplay = async (params: IConfigurePassportDisplayDTO): Promise<IConfigurePassportDisplayDTO> => {
        const response = await this.apiClient.post<IConfigurePassportDisplayDTO>(CONNECTIONS_API.ROOM_PASSPORT_CONFIGURE_DISPLAY, params);
        return response.parsedResponse;
    };

    public async scheduleNewPanelistLink(params: IPanelistLinkConnectionInfo): Promise<IPanelistLinkResponse> {
        const response = await this.apiClient.post<IPanelistLinkResponse>(CREATE_PANELIST_LINK, {
            webinarId: params.webinarID,
            email: params.email,
            name: params.attendeeName,
        });
        return response.parsedResponse;
    }

    public async updateChildConnection(params: IEventRequestParams & Partial<IChildConnectionDTO>): Promise<IChildConnectionDTO> {
        const response = await this.apiClient.put<IChildConnectionDTO>(CONNECTIONS_API.CHILD_CONNECTION_URL, params);
        return response.parsedResponse;
    };

    public async deleteChildConnection(params: IEventRequestParams & { joinMethodId: number }): Promise<void> {
        const response = await this.apiClient.delete<void>(CONNECTIONS_API.CHILD_CONNECTION_URL, params);
        return response.parsedResponse;
    };
}

export { ConnectionsDAO };
